import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Sparkles, Send, AlertTriangle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { STATUSES } from './leadUtils';
import { devWarn } from '../../lib/log';

export const MarketingTab = ({ authAxios }) => {
  const [smtpReady, setSmtpReady] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [audience, setAudience] = useState('all');
  const [prompt, setPrompt] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const [settingsRes, campaignsRes] = await Promise.all([
        authAxios.get('/admin/settings'),
        authAxios.get('/admin/campaigns'),
      ]);
      setSmtpReady(!!(settingsRes.data.smtp_host && settingsRes.data.smtp_password));
      setCampaigns(campaignsRes.data.campaigns || []);
    } catch (e) {
      // 401 is redirected by the interceptor; log anything else.
      if (e?.response?.status !== 401) devWarn('Marketing data load failed:', e);
    }
  }, [authAxios]);

  useEffect(() => {
    load();
  }, [load]);

  const generate = async () => {
    if (!prompt.trim()) return;
    setGenerating(true);
    try {
      const res = await authAxios.post('/admin/ai/campaign-draft', {
        prompt: prompt.trim(),
        audience: audience === 'all' ? 'all leads with email' : `leads with status ${audience}`,
      }, { timeout: 90000 });
      setSubject(res.data.subject);
      setBody(res.data.body);
      toast.success('Draft generated — review and edit before sending');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'AI draft failed');
    } finally {
      setGenerating(false);
    }
  };

  const sendCampaign = async () => {
    if (!subject.trim() || !body.trim()) return;
    setSending(true);
    try {
      const res = await authAxios.post('/admin/campaigns/send', {
        subject: subject.trim(),
        body,
        status_filter: audience === 'all' ? null : audience,
      }, { timeout: 180000 });
      toast.success(`Campaign sent to ${res.data.sent_count} lead(s)${res.data.failed_count ? `, ${res.data.failed_count} failed` : ''}`);
      setSubject('');
      setBody('');
      setPrompt('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Campaign send failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6">
      {smtpReady === false && (
        <div className="flex items-start gap-3 bg-[rgba(184,115,51,0.08)] border border-[rgba(184,115,51,0.3)] rounded-xl p-4 text-sm" data-testid="marketing-smtp-warning">
          <AlertTriangle className="w-4.5 h-4.5 text-[#B87333] shrink-0 mt-0.5" size={18} />
          <p>
            <span className="font-medium">SMTP not configured.</span> Add your Zoho SMTP credentials in the Settings tab
            (host <span className="font-mono-tech">smtp.zoho.com</span> or <span className="font-mono-tech">smtp.zoho.in</span>, port 587)
            before sending campaigns. You can still generate AI drafts now.
          </p>
        </div>
      )}

      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="font-display font-semibold text-lg flex items-center gap-2">
          <Sparkles className="w-4.5 h-4.5 text-[#B87333]" size={18} /> AI Email Campaign
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          Describe the campaign — AI writes a premium, on-brand email. Review and edit before sending. Use <span className="font-mono-tech">{'{name}'}</span> in the body to personalise with each lead's name.
        </p>

        <div className="mt-5 grid sm:grid-cols-[1fr_200px] gap-4">
          <div>
            <label className="text-sm font-medium">Campaign brief</label>
            <Textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="e.g. Invite leads to explore EPS farmhouse construction for weekend homes near Bengaluru, highlight speed and thermal comfort"
              className="mt-1.5 min-h-[80px]"
              data-testid="marketing-prompt-input"
            />
          </div>
          <div>
            <label className="text-sm font-medium">Audience</label>
            <Select value={audience} onValueChange={setAudience}>
              <SelectTrigger className="h-10 mt-1.5" data-testid="marketing-audience-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All leads with email</SelectItem>
                {STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>Status: {s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              className="w-full h-10 mt-3"
              variant="outline"
              disabled={!prompt.trim() || generating}
              onClick={generate}
              data-testid="marketing-generate-button"
            >
              {generating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
              Generate with AI
            </Button>
          </div>
        </div>

        <div className="mt-5 space-y-4">
          <div>
            <label className="text-sm font-medium">Subject</label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Email subject" className="h-10 mt-1.5" data-testid="marketing-subject-input" />
          </div>
          <div>
            <label className="text-sm font-medium">Body</label>
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Email body (plain text)" className="mt-1.5 min-h-[180px]" data-testid="marketing-body-input" />
          </div>
          <Button
            className="h-11 px-6"
            disabled={!subject.trim() || !body.trim() || sending || smtpReady === false}
            onClick={sendCampaign}
            data-testid="marketing-send-button"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
            Send Campaign
          </Button>
        </div>
      </div>

      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="font-display font-semibold text-lg">Campaign History</h3>
        {campaigns.length === 0 ? (
          <p className="mt-4 text-sm text-muted-foreground" data-testid="marketing-history-empty">No campaigns sent yet.</p>
        ) : (
          <div className="mt-4 divide-y divide-border">
            {campaigns.map((c) => (
              <div key={c.id} className="py-4" data-testid={`marketing-campaign-${c.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <p className="font-medium text-sm">{c.subject}</p>
                  <Badge variant="outline" className="font-mono-tech text-[10px] shrink-0">
                    {c.sent_count} sent{c.failed_count ? ` · ${c.failed_count} failed` : ''}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Audience: {c.status_filter} · {new Date(c.created_at).toLocaleString('en-IN')}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
